import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  User, Package, Wallet, Trophy, Leaf, ArrowRight, Download, 
  Eye, Share2, Plus, LogOut, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MyProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Get user from localStorage
  const irisId = localStorage.getItem('ginsgood_iris_id');
  const userName = localStorage.getItem('ginsgood_iris_name');

  useEffect(() => {
    if (!irisId) {
      navigate('/iris-auth');
      return;
    }
    fetchProfile();
  }, [irisId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/user/${irisId}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ginsgood_iris_id');
    localStorage.removeItem('ginsgood_iris_name');
    localStorage.removeItem('ginsgood_user_type');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const copyReferralCode = async () => {
    if (profile?.referral_code) {
      await navigator.clipboard.writeText(profile.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Referral code copied!');
    }
  };

  const downloadPdf = async (certId, productName) => {
    setDownloadingPdf(certId);
    try {
      const response = await axios.get(`${API}/passport/${certId}/download-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GinsGood_Certificate_${productName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const shareCertificate = async (certId) => {
    const url = `${window.location.origin}/passport/${certId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Certificate link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      platinum: 'bg-slate-100 text-slate-800 border-slate-300',
      gold: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      silver: 'bg-gray-100 text-gray-800 border-gray-300',
      bronze: 'bg-orange-100 text-orange-800 border-orange-300',
      uncertified: 'bg-gray-100 text-gray-500 border-gray-200'
    };
    return colors[tier] || colors.uncertified;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6C4C] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <p className="text-gray-600 mb-4">Please log in to view your profile</p>
              <Button onClick={() => navigate('/iris-auth')}>Log In</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          {/* Profile Header */}
          <Card className="mb-8 overflow-hidden">
            <div className="bg-gradient-to-r from-[#4A6C4C] to-[#6B8E6B] p-8 text-white">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-3xl font-bold">
                    {profile.user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold">{profile.user?.name || userName}</h1>
                    <p className="text-green-100 capitalize">{profile.user?.user_type || 'Seller'}</p>
                    <Badge className="mt-2 bg-white/20 text-white border-white/30">
                      {profile.tier}
                    </Badge>
                  </div>
                </div>
                <Button variant="secondary" onClick={handleLogout} className="gap-2">
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-6 bg-gray-50">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#4A6C4C]">{profile.certificate_count}</div>
                <div className="text-sm text-gray-600">Certificates</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#4A6C4C]">{profile.gsg_balance?.toLocaleString()}</div>
                <div className="text-sm text-gray-600">GSG Balance</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#4A6C4C]">{profile.carbon_offset_total || 0}</div>
                <div className="text-sm text-gray-600">kg CO2 Offset</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#4A6C4C]">{profile.achievement_count}</div>
                <div className="text-sm text-gray-600">Achievements</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#4A6C4C]">{profile.referral_uses}</div>
                <div className="text-sm text-gray-600">Referrals</div>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Button 
              onClick={() => navigate('/certify')} 
              className="h-auto py-4 flex-col gap-2 bg-[#4A6C4C] hover:bg-[#3d5940]"
              data-testid="certify-product-btn"
            >
              <Plus className="w-6 h-6" />
              <span>Certify Product</span>
            </Button>
            <Button 
              onClick={() => navigate('/wallet')} 
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
            >
              <Wallet className="w-6 h-6" />
              <span>My Wallet</span>
            </Button>
            <Button 
              onClick={() => navigate('/achievements')} 
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
            >
              <Trophy className="w-6 h-6" />
              <span>Achievements</span>
            </Button>
            <Button 
              onClick={() => navigate('/carbon')} 
              variant="outline"
              className="h-auto py-4 flex-col gap-2"
            >
              <Leaf className="w-6 h-6" />
              <span>Carbon Impact</span>
            </Button>
          </div>

          {/* Referral Code Card */}
          {profile.referral_code && (
            <Card className="mb-8 bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-amber-900">Your Referral Code</h3>
                    <p className="text-sm text-amber-700">Share this code and earn GSG when friends join!</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="px-4 py-2 bg-white rounded-lg font-mono text-lg border border-amber-200">
                      {profile.referral_code}
                    </code>
                    <Button size="icon" variant="outline" onClick={copyReferralCode}>
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabs for Certificates */}
          <Tabs defaultValue="certificates" className="space-y-6">
            <TabsList>
              <TabsTrigger value="certificates" className="gap-2">
                <Package className="w-4 h-4" />
                My Certificates ({profile.certificate_count})
              </TabsTrigger>
              <TabsTrigger value="achievements" className="gap-2">
                <Trophy className="w-4 h-4" />
                Achievements ({profile.achievement_count})
              </TabsTrigger>
            </TabsList>

            {/* Certificates Tab */}
            <TabsContent value="certificates">
              {profile.certificates?.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No certificates yet</h3>
                    <p className="text-gray-500 mb-4">Certify your first product to get started</p>
                    <Button onClick={() => navigate('/certify')} className="gap-2 bg-[#4A6C4C]">
                      <Plus className="w-4 h-4" />
                      Certify Your First Product
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {profile.certificates.map((cert) => (
                    <Card 
                      key={cert.id} 
                      className="hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => navigate(`/passport/${cert.id}`)}
                      data-testid={`cert-card-${cert.id}`}
                    >
                      <CardContent className="p-4">
                        {cert.product_image_url && (
                          <div className="aspect-video bg-gray-100 rounded-lg mb-3 overflow-hidden">
                            <img 
                              src={cert.product_image_url} 
                              alt={cert.product_name}
                              className="w-full h-full object-cover"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate">{cert.product_name}</h3>
                            <p className="text-sm text-gray-500">{cert.certificate_number}</p>
                          </div>
                          <Badge className={getTierColor(cert.tier)}>
                            {cert.tier_icon} {cert.tier_display}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-2xl font-bold text-[#4A6C4C]">{cert.score?.toFixed(1)}%</span>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={(e) => { e.stopPropagation(); navigate(`/passport/${cert.id}`); }}
                              title="View Passport"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={(e) => { e.stopPropagation(); downloadPdf(cert.id, cert.product_name); }}
                              disabled={downloadingPdf === cert.id}
                              title="Download PDF"
                            >
                              {downloadingPdf === cert.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={(e) => { e.stopPropagation(); shareCertificate(cert.id); }}
                              title="Share Certificate"
                            >
                              <Share2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          Certified: {new Date(cert.certified_at || cert.issued_at).toLocaleDateString()}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Achievements Tab */}
            <TabsContent value="achievements">
              {profile.achievements?.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No achievements yet</h3>
                    <p className="text-gray-500 mb-4">Complete actions to earn achievements and GSG rewards</p>
                    <Button onClick={() => navigate('/achievements')} variant="outline" className="gap-2">
                      View All Achievements
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {profile.achievements.map((achievement, idx) => (
                    <Card key={idx} className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
                      <CardContent className="p-4 text-center">
                        <div className="text-4xl mb-2">{achievement.icon || '🏆'}</div>
                        <h3 className="font-semibold text-amber-900">{achievement.name}</h3>
                        <p className="text-sm text-amber-700">{achievement.description}</p>
                        <Badge className="mt-2 bg-amber-100 text-amber-800">
                          +{achievement.gsg_reward} GSG
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Public Profile Link */}
          <Card className="mt-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Your Public Seller Profile</h3>
                  <p className="text-sm text-gray-500">Share this link on Tudugu or other marketplaces</p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/seller/${irisId}`)}
                  className="gap-2"
                >
                  View Public Profile
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default MyProfilePage;
